import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AttendanceEventType, AttendanceWorkplaceType, workplaceTypeFromAttendanceKind } from "./attendanceConstants";
import { detectClientDeviceType } from "./attendanceApi";
import { Common } from "../../../utils";
import "./Attendance.css";

const KIND = {
  regular: "regular",
  remote: "remote",
  field: "field",
};

/** @typedef {'idle' | 'loading' | 'ready' | 'denied' | 'unavailable'} GeoStatus */

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {'checkIn' | 'checkOut'} props.mode
 * @param {(payload: { eventType: number, clientDeviceType: number, workplaceType: number, projectIds?: string[], location?: string, note?: string }) => Promise<void>} props.onConfirm
 * @param {{ id: string, name: string }[]} props.projects
 * @param {boolean} props.loading
 * @param {boolean} props.requireUserLocation
 */
const AttendanceClockModal = ({
  open,
  onClose,
  mode,
  onConfirm,
  projects,
  loading,
  requireUserLocation = false,
}) => {
  const { t } = useTranslation();
  const [kind, setKind] = useState(KIND.remote);
  /** @type {[string[], React.Dispatch<React.SetStateAction<string[]>>]} */
  const [projectIds, setProjectIds] = useState(/** @type {string[]} */ ([]));
  const [note, setNote] = useState("");
  /** @type {[GeoStatus, React.Dispatch<React.SetStateAction<GeoStatus>>]} */
  const [geoStatus, setGeoStatus] = useState(/** @type {GeoStatus} */ ("idle"));
  const [geoCoords, setGeoCoords] = useState(/** @type {{ lat: number; lng: number } | null} */ (null));

  useEffect(() => {
    if (open) {
      setKind(KIND.remote);
      setProjectIds([]);
      setNote("");
      setGeoStatus("idle");
      setGeoCoords(null);
    }
  }, [open, mode]);

  useEffect(() => {
    if (!open || mode !== "checkIn") return;
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGeoStatus("unavailable");
      setGeoCoords(null);
      return;
    }
    let cancelled = false;
    setGeoStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        setGeoCoords({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        setGeoStatus("ready");
      },
      (err) => {
        if (cancelled) return;
        const code = err && typeof err.code === "number" ? err.code : 0;
        setGeoCoords(null);
        setGeoStatus(code === 1 ? "denied" : "unavailable");
      },
      {
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 0,
      }
    );
    return () => {
      cancelled = true;
    };
  }, [open, mode]);

  const title =
    mode === "checkOut"
      ? t("app.attendance.modal.checkOutTitle")
      : t("app.attendance.modal.checkInTitle");

  /**
   * Note body is only free text. Workplace type is sent separately (`WorkplaceType`, `Location`);
   * do not duplicate labels like `[Remote]` here — they were redundant and confused reports.
   */
  const buildNoteForSubmit = () => {
    let n = note.trim();
    if (mode !== "checkIn" || !geoCoords) return n;

    const appendCoords =
      kind === KIND.remote ||
      kind === KIND.field ||
      requireUserLocation;

    if (!appendCoords) return n;

    const geoLine = `[Coordinates: ${geoCoords.lat.toFixed(6)}, ${geoCoords.lng.toFixed(6)}]`;
    return n ? `${n}\n${geoLine}` : geoLine;
  };

  const checkInBlockedByLocation = useMemo(
    () =>
      mode === "checkIn" &&
      requireUserLocation &&
      (geoStatus !== "ready" || !geoCoords),
    [mode, requireUserLocation, geoStatus, geoCoords]
  );

  const geoResolving = mode === "checkIn" && geoStatus === "loading";
  const confirmBusy = loading || geoResolving;

  const toggleProject = (id) => {
    const key = String(id);
    setProjectIds((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  };

  const handleSubmit = async () => {
    if (checkInBlockedByLocation) {
      Common.showErrorMessage(t("app.attendance.modal.checkInNeedsLocation"));
      return;
    }

    const eventType =
      mode === "checkOut" ? AttendanceEventType.CheckOut : AttendanceEventType.CheckIn;

    const location =
      mode === "checkIn"
        ? kind === KIND.remote
          ? "Remote"
          : kind === KIND.field
            ? "Field visit"
            : ""
        : "";

    const workplaceType =
      mode === "checkIn"
        ? workplaceTypeFromAttendanceKind(kind)
        : AttendanceWorkplaceType.Office;

    await onConfirm({
      eventType,
      clientDeviceType: detectClientDeviceType(),
      workplaceType,
      projectIds: mode === "checkIn" && projectIds.length > 0 ? projectIds : undefined,
      location: location || undefined,
      note: buildNoteForSubmit() || undefined,
    });
  };

  if (!open) return null;

  return (
    <div className="att-modal-overlay" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="att-modal" role="dialog" aria-modal="true" aria-labelledby="att-clock-modal-title">
        <div className="att-modal-header">
          <div className="att-modal-title-row">
            <div className="att-modal-icon-wrap" aria-hidden>
              <i className={`bi ${mode === "checkOut" ? "bi-box-arrow-right" : "bi-box-arrow-in-right"}`} />
            </div>
            <h2 id="att-clock-modal-title" className="att-modal-title">
              {title}
            </h2>
          </div>
          <button type="button" className="att-modal-close" onClick={onClose} aria-label={t("app.common.close")}>
            ×
          </button>
        </div>
        <div className="att-modal-body">
          {mode === "checkIn" && (
            <>
              <div className="att-clock-loc-card att-clock-loc-card--readonly">
                <div className="att-clock-loc-icon" aria-hidden>
                  <i className="bi bi-geo-alt-fill" />
                </div>
                <div className="att-clock-loc-text flex-grow-1 min-w-0">
                  <div className="att-clock-loc-title">{t("app.attendance.modal.locationContextTitle")}</div>
                  <div className="att-clock-loc-hint text-muted small">
                    {requireUserLocation
                      ? t("app.attendance.modal.locationContextHintRequired")
                      : t("app.attendance.modal.locationContextHint")}
                  </div>
                  {geoStatus !== "idle" && (
                    <div className="att-clock-loc-geo small mt-1">
                      {geoStatus === "loading" && t("app.attendance.modal.locationGeoLoading")}
                      {geoStatus === "ready" && geoCoords && (
                        <>
                          <span className="d-block">{t("app.attendance.modal.locationGeoReady")}</span>
                          <span className="att-clock-loc-coords d-block mt-1">
                            {geoCoords.lat.toFixed(5)}, {geoCoords.lng.toFixed(5)}
                          </span>
                        </>
                      )}
                      {geoStatus === "ready" && !geoCoords && t("app.attendance.modal.locationGeoReady")}
                      {geoStatus === "denied" && t("app.attendance.modal.locationGeoDenied")}
                      {geoStatus === "unavailable" && t("app.attendance.modal.locationGeoUnavailable")}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <span className="att-label-upper">{t("app.attendance.modal.attendanceType")}</span>
                <div className="att-segment-toggle">
                  <button
                    type="button"
                    className={kind === KIND.remote ? "att-is-active" : ""}
                    onClick={() => setKind(KIND.remote)}
                  >
                    {t("app.attendance.modal.typeRemote")}
                  </button>
                  <button
                    type="button"
                    className={kind === KIND.regular ? "att-is-active" : ""}
                    onClick={() => setKind(KIND.regular)}
                  >
                    {t("app.attendance.modal.typeRegular")}
                  </button>
                  <button
                    type="button"
                    className={kind === KIND.field ? "att-is-active" : ""}
                    onClick={() => setKind(KIND.field)}
                  >
                    {t("app.attendance.modal.typeField")}
                  </button>
                </div>
              </div>

              <div className="att-modal-field-white">
                <span className="att-label-upper">{t("app.attendance.modal.project")}</span>
                <p className="att-project-multi-hint text-muted small mb-2">
                  {t("app.attendance.modal.projectMultiHint")}
                </p>
                {(!projects || projects.length === 0) ? (
                  <p className="att-project-multi-empty text-muted small mb-0">
                    {t("app.attendance.modal.projectOptional")}
                  </p>
                ) : (
                  <div className="att-project-checklist" role="group" aria-label={t("app.attendance.modal.project")}>
                    {projects.map((p) => {
                      const id = String(p.id);
                      const checked = projectIds.includes(id);
                      return (
                        <label key={id} className={`att-project-check-item${checked ? " is-checked" : ""}`}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={checked}
                            onChange={() => toggleProject(id)}
                          />
                          <span className="att-project-check-label">{p.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          <div className="att-modal-field-white">
            <span className="att-label-upper">
              {mode === "checkOut" ? t("app.attendance.modal.notesCheckOut") : t("app.attendance.modal.notes")}
            </span>
            <textarea
              className="att-textarea att-textarea--white"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("app.attendance.modal.notesPlaceholder")}
            />
          </div>
        </div>
        <div className="att-modal-footer">
          <button type="button" className="att-btn att-btn--ghost" onClick={onClose} disabled={loading}>
            {t("app.common.cancel")}
          </button>
          <button
            type="button"
            className="att-btn att-btn--save-primary"
            onClick={handleSubmit}
            disabled={confirmBusy || checkInBlockedByLocation}
          >
            {confirmBusy
              ? t("app.common.loading")
              : mode === "checkOut"
                ? t("app.attendance.modal.confirmCheckOut")
                : t("app.attendance.modal.confirmCheckIn")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttendanceClockModal;
